import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle } from 'lucide-react';
import MainNav from '@/components/MainNav';
import AccessibilityWidget from '@/components/AccessibilityWidget';
import { useToast } from '@/hooks/use-toast';
import Footer from '@/components/Footer';

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [isVerifying, setIsVerifying] = useState(true);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setIsVerifying(false);
      return;
    }

    const verifySession = async () => {
      try {
        // Verify the session
        const response = await fetch(
          `https://lmniterbzhfihvxsumbl.supabase.co/functions/v1/verify-session?session_id=${sessionId}`
        );

        if (!response.ok) {
          throw new Error('Failed to verify session');
        }

        toast({
          title: "Success!",
          description: "Your subscription has been activated.",
        });
      } catch (error) {
        console.error('Verification error:', error);
        toast({
          title: "Verification Error",
          description: "There was a problem verifying your subscription. Please contact support.",
          variant: "destructive"
        });
      } finally {
        setIsVerifying(false);
      }
    };

    verifySession();
  }, [searchParams, toast]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <AccessibilityWidget />
      <header>
        <MainNav currentPath="success" />
      </header>
      
      <main className="container max-w-4xl mx-auto px-4 py-12">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900 mb-6">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-300" />
          </div>
          
          <h1 className="text-3xl font-bold mb-4">
            Thank You for Your Subscription!
          </h1>
          
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            {isVerifying 
              ? "We're verifying your subscription..."
              : "Your subscription has been confirmed. You can now start using Accesstopia on your websites."}
          </p>
          
          <div className="space-x-4">
            <Button asChild>
              <Link to="/installation">
                Get Installation Code
              </Link>
            </Button>
            
            <Button asChild variant="outline">
              <Link to="/account">
                View Account
              </Link>
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SuccessPage;
